import {
  Box,
  Chip,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SVButton from '../SVButton';
import { Add, Close, Search } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { SearchParamsObject, SVSelectProps } from '@/types/common';
import { useBrandingTheme } from '@/hooks/stores/useBrandingTheme';
import SVAutocomplete from '../inputs/SVAutocomplete';

type SVListFiltersProps = {
  defaultValues: DefaultValues<SearchParamsObject>;
  filters: SVSelectProps[];
  searchLabel?: string;
};

export default function SVListFilters({
  defaultValues,
  filters,
  searchLabel,
}: SVListFiltersProps) {
  const { mode } = useBrandingTheme();
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const [open, setOpen] = useState(false);
  const [chips, setChips] = useState<SearchParamsObject[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSearch = searchParams.get('search') || '';

  const formMethods = useForm<SearchParamsObject>({
    defaultValues,
    mode: 'onBlur',
  });

  const debouncedHandleChange = useCallback(
    debounce(event => {
      const { value } = event.target;
      setSearchParams(
        !value
          ? ''
          : {
              search: value,
            },
      );
    }, 2000),
    [searchParams],
  );

  function buildChipsArray(formValues: SearchParamsObject) {
    const chipsArray = [];
    for (const [key, value] of Object.entries(formValues)) {
      if (key === 'page') {
        continue;
      }

      if (!value) {
        return;
      }

      const matchingFilter = filters.find(filter => filter.name === key);
      if (!matchingFilter) {
        return;
      }

      const matchingOption = matchingFilter.options.find(
        option => option.value === value,
      );
      if (!matchingOption) {
        return;
      }

      chipsArray.push({
        key,
        label: matchingFilter.label,
        value: matchingOption.label,
      });
    }

    return chipsArray;
  }

  useEffect(() => {
    const copiedSearchParams = Object.fromEntries(searchParams.entries());
    const chips = buildChipsArray(copiedSearchParams) || [];
    setChips(chips);
  }, [searchParams]);

  function applyFilters() {
    const data = formMethods.getValues();
    const cleanedData: SearchParamsObject = { ...data };
    Object.keys(cleanedData).forEach(key => {
      const value = cleanedData[key];
      if (value === null || value === undefined || value === '') {
        delete cleanedData[key];
      }
    });

    setSearchParams(cleanedData);
    setOpen(false);
  }

  function removeFilter(key: string) {
    const copiedSearchParams = Object.fromEntries(searchParams.entries());
    delete copiedSearchParams[key];
    formMethods.setValue(key, '');

    setSearchParams(copiedSearchParams);
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column',
          justifyContent: 'space-between',
          mb: 1,
          color: mode === 'light' ? 'black' : 'white',
        }}>
        <FormProvider {...formMethods}>
          <Stack>
            <Typography fontWeight="bold" m={0} p={0}>
              {searchLabel ? searchLabel : 'Search'}
            </Typography>
            <TextField
              aria-label={searchLabel ? searchLabel : 'Search'}
              sx={{
                background: mode === 'light' ? 'white' : '#121212',
                width: 450,
                maxWidth: '100%',
              }}
              onChange={event => {
                debouncedHandleChange(event);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                },
              }}
              defaultValue={defaultSearch}
            />
          </Stack>
          <SVButton
            onClick={() => setOpen(true)}
            variant="text"
            startIcon={<Add />}
            sx={{
              ...(isLargeScreen ? {} : { py: 2 }),
            }}>
            Add Filters
          </SVButton>
          <Dialog
            fullWidth={isLargeScreen}
            fullScreen={!isLargeScreen}
            open={open}
            onClose={() => setOpen(false)}>
            <DialogContent>
              <Box>
                <Typography variant="h6" component="h2" mb={2}>
                  Add Filters
                </Typography>
                {filters.map(filter => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                    }}
                    key={filter.name}>
                    <Box width="100%">
                      <SVAutocomplete {...filter} />
                    </Box>
                    <IconButton
                      sx={{ p: 0, mt: '12px', height: 40 }}
                      onClick={() => formMethods.setValue(filter.name, '')}>
                      <Close />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <SVButton onClick={() => setOpen(false)} variant="tertiary">
                Close
              </SVButton>
              <SVButton onClick={formMethods.handleSubmit(applyFilters)}>
                Apply
              </SVButton>
            </DialogActions>
          </Dialog>
        </FormProvider>
      </Box>
      {chips.map(chip => (
        <Chip
          key={chip.key}
          data-testid={`chip-${chip.value}`}
          label={
            <Typography variant="caption" fontWeight="bold">
              {chip.label}:{' '}
              <Typography
                variant="caption"
                component="span"
                fontWeight="normal">
                {chip.value}
              </Typography>
            </Typography>
          }
          onDelete={() => removeFilter(chip.key)}
          sx={{ mr: 1 }}
        />
      ))}
    </Box>
  );
}
